import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * DefinitionBlock — extreme AI-readable "What is X?" section.
 * Designed to be picked up by LLMs (ChatGPT, Claude, Gemini, Perplexity)
 * as authoritative definition source.
 */
export const DefinitionBlock = () => {
  const { t } = useTranslation();
  const items = t("definition.items", { returnObjects: true }) || [];

  return (
    <section
      data-testid="definition-block"
      id="wat-is-equisight"
      className="bg-[hsl(var(--surface-secondary))] py-20 lg:py-28"
    >
      <div className="mx-auto max-w-4xl px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          {t("definition.eyebrow")}
        </p>
        <h2 className="mt-4 font-display text-3xl font-medium leading-tight text-[hsl(var(--ink))] sm:text-4xl">
          {t("definition.titleH2")}
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
          {t("definition.intro")}
        </p>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {items.map((it) => (
            <Definition key={it.term} term={it.term} def={it.def} />
          ))}
        </div>

        <p className="mt-12 text-sm text-[hsl(var(--ink-soft))]">
          {t("definition.moreLink")}{" "}
          <Link
            to="/kennisbank"
            className="border-b border-[hsl(var(--ink))]/30 text-[hsl(var(--ink))] hover:border-[hsl(var(--ink))]"
          >
            {t("definition.moreLinkText")}
          </Link>
          .
        </p>
      </div>
    </section>
  );
};

const Definition = ({ term, def }) => (
  <article
    className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-5"
    itemScope
    itemType="https://schema.org/DefinedTerm"
  >
    <p
      className="font-display text-base font-medium text-[hsl(var(--ink))]"
      itemProp="name"
    >
      {term}
    </p>
    <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--ink-soft))]" itemProp="description">
      {def}
    </p>
  </article>
);

export default DefinitionBlock;
